import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { statusLabel, statusColor } from '../../lib/statusCatalog';
import {
  ArrowRight,
  Package,
  Truck,
  RotateCcw,
  Wrench,
  Clock,
  User,
  FileText,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

const ConditionHistoryModal = ({ open, onOpenChange, equipmentId, equipmentName }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && equipmentId) {
      loadHistory();
    }
  }, [open, equipmentId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await api().get(`/equipment/${equipmentId}/condition-history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading condition history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    return (
      <Badge className={statusColor(status)}>
        {statusLabel(status)}
      </Badge>
    );
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'checkout':
        return <Truck className="h-4 w-4 text-blue-400" />;
      case 'return':
        return <RotateCcw className="h-4 w-4 text-emerald-400" />;
      case 'maintenance':
        return <Wrench className="h-4 w-4 text-amber-400" />;
      case 'manual':
        return <User className="h-4 w-4 text-gray-400" />;
      default:
        return <Package className="h-4 w-4 text-gray-400" />;
    }
  };

  const getChangeTypeLabel = (type) => {
    const labels = {
      checkout: 'Salida',
      return: 'Retorno',
      maintenance: 'Mantenimiento',
      manual: 'Cambio Manual',
      repair: 'Reparación'
    };
    return labels[type] || type;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Historial de Condiciones
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {equipmentName}
          </p>
        </DialogHeader>

        <ScrollArea className="h-[500px] pr-4">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No hay historial de cambios</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
              
              <div className="space-y-4">
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative pl-10">
                    {/* Timeline dot */}
                    <div className="absolute left-2 top-2 w-5 h-5 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      {getChangeTypeIcon(entry.change_type)}
                    </div>
                    
                    <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {getChangeTypeLabel(entry.change_type)}
                          </Badge>
                          {entry.serial_number && (
                            <span className="text-xs font-mono text-muted-foreground">
                              #{entry.serial_number}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      
                      {/* Status Change */}
                      <div className="flex items-center gap-2 mb-3">
                        {getStatusBadge(entry.previous_status)}
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        {getStatusBadge(entry.new_status)}
                      </div>
                      
                      {/* Details */}
                      <div className="space-y-1 text-sm">
                        {entry.changed_by_name && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="h-3.5 w-3.5" />
                            <span>Por: {entry.changed_by_name}</span>
                          </div>
                        )}
                        {entry.movement_type && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <FileText className="h-3.5 w-3.5" />
                            <span>Movimiento: {entry.movement_type}</span>
                          </div>
                        )}
                        {entry.reason && (
                          <div className="flex items-start gap-2 text-muted-foreground">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-500" />
                            <span>{entry.reason}</span>
                          </div>
                        )}
                        {entry.notes && entry.notes.trim() && (
                          <div className="flex items-start gap-2 text-muted-foreground">
                            <CheckCircle className="h-3.5 w-3.5 mt-0.5" />
                            <span className="italic">{entry.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ConditionHistoryModal;
